/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { call, delay, put, race } from "redux-saga/effects";
import {
  IGetStateProvince,
  IGetStateProvinceSchool,
  IGetStateProvinceShipping,
  getStateProvinceFailure,
  getStateProvinceSuccess,
  getStateProvinceShippingFailure,
  getStateProvinceShippingSuccess,
  getStateProvinceSchoolFailure,
  getStateProvinceSchoolSuccess,
} from "./StateProvinceAction";
import { IApiErrorResult, IApiResult2 } from "../../Utility/IApiResult";
import { HttpStatusCode } from "../../Constants/HttpStatusCode";
import {
  IGetStateProvinceDataResult,
  IGetStateProvinceOkResult,
} from "../../Services/Interfaces/SiteContent";
import * as siteContentApi from "../../Services/SiteContentApi";
import { GENERIC_API_TIMEOUT_ERROR } from "../../Constants/ApplicationConstants";
import { getDefaultApiTimeOutConfiguration } from "../../Utility/Utils";

function* handleLoadStateProvincesSchool(action: IGetStateProvinceSchool) {
  try {
    const { result, timeout }: IApiResult2<IGetStateProvinceDataResult> | any = yield race({
      result: call(siteContentApi.fetchStateProvinceList, action.countryRegionName),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getStateProvinceSchoolFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getStateProvinceSchoolSuccess(
            (result.parsedBody as IGetStateProvinceOkResult).stateProvinces
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getStateProvinceSchoolFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(
          getStateProvinceSchoolFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
    }
  } catch (error) {
    yield put(getStateProvinceSchoolFailure(error.message));
  }
}

function* handleLoadStateProvincesShipping(action: IGetStateProvinceShipping) {
  try {
    const { result, timeout }: IApiResult2<IGetStateProvinceDataResult> | any = yield race({
      result: call(siteContentApi.fetchStateProvinceList, action.countryRegionName),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getStateProvinceShippingFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getStateProvinceShippingSuccess(
            (result.parsedBody as IGetStateProvinceOkResult).stateProvinces
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          getStateProvinceShippingFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      case HttpStatusCode.NotFound:
        yield put(
          getStateProvinceShippingFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(
          getStateProvinceShippingFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
    }
  } catch (error) {
    yield put(getStateProvinceShippingFailure(error.message));
  }
}

function* handleLoadStateProvinces(action: IGetStateProvince) {
  try {
    const { result, timeout }: IApiResult2<IGetStateProvinceDataResult> | any = yield race({
      result: call(siteContentApi.fetchStateProvinceList, action.countryRegionName),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getStateProvinceFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getStateProvinceSuccess((result.parsedBody as IGetStateProvinceOkResult).stateProvinces)
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(getStateProvinceFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.NotFound:
        yield put(getStateProvinceFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(getStateProvinceFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
    }
  } catch (error) {
    yield put(getStateProvinceFailure(error.message));
  }
}

/**
 *
 */
export {
  handleLoadStateProvinces,
  handleLoadStateProvincesShipping,
  handleLoadStateProvincesSchool,
};
